import config from '@/config'
import survey from '@/data/survey.js'
import { i18n } from '@/i18n'
import api from '@/api'
import { ANALYTIC_AGE_TO_AGE_RANGE_MAP, VALUES_MAP_FOR_LEGACY_ANALYTICS_BY_PROPERTY } from '@/helpers/constants'
import { ENV_DEV_OR_STAGING } from '@/helpers/environment'
import { convertHeight, convertWeights } from '@/helpers/userData'
import { mlChurnPredictRequest } from '@/helpers/request'
import { os } from '@/helpers/device'
import * as Sentry from '@sentry/browser'

export default {
	namespaced: true,

	state: {
		survey,
		currentStep: null,
		step_progress: null,

		values: {
			height: null,
			weight: null,
			birthDate: null,
			targetWeight: null,
			secondsFromGMT: null,
			sex: '', //male
			intermittentFasting: [],
			isEmailSubscriptionDisabled: null,
			consents: [],

			data: {
				age: null,
				goal: ['lose_weight'],
				gender: [],
				strategy: [],
				lunch: [],
				feelings: [],
				hopings: [],
				happy_weight_1: [],
				happy_weight_2: [],
				meal_shedule: [],
				diet: [],
				meals: [],
				work: null,
				sport: ['not_at_all'],
				sleep: [],
				diseases: [],
				liveProblem: [],
				drugs: [],
				bodyType: null,
				carbs: [],
				waterDrink: null,
				measurement_system: null,
				measurement_system_height: null,
				measurement_system_weight: null,
				area: [],
				rq_1: [],
				rq_2: [],
				rq_3: [],
				rq_4: [],
				adt: [],
				motivation_event: [],
				motivation_event_custom: '',
				specialEvent: null,
				fromOnePage: false,
				planUp: false,
				planDown: false,
				following_statement_1: null,
				following_statement_2: null,
				following_statement_3: null,
				following_statement_4: null,
				following_statement_5: null,
				following_statement_6: null,
				intermittent_fasting: [],
				priorites: [],
				chronotype: [],
				cancel_reason: [],
				second_goal: [],

				at_happy_weight: [],
				confidence_in_reaching: [],
				current_priorities: [],
				eating_habits: [],
				gain_weight_reason: [],
				last_happy_weight: [],
				like_to_boost: [],
				like_to_feel: [],
				main_reason: [],
				mental_health_services: [],
				more_to_explore: [],
				motivation_to_lose_weight: [],
				see_my_self: [],
				snacking_triggers: [],
				typical_day: [],
				typical_meal: [],
				understanding_mindset: [],
				weight_loss_apps: [],
				weight_loss_attempts: [],
				weight_loss_goal: [],
				weight_loss_goals: [],
				what_make_uncertain: [],
				your_plan_ready: [],
				eating_disorder: [],
				pregnancy: [],
				gender_identity: null,
				non_binary_identity: null
			}
		}
	},

	getters: {
		getStep: (state) => (property) => {
			return state.survey.find((step) => step.id === property)
		},

		getStepByProperty: (state) => (property) => {
			return state.survey.find((step) => step.property === property)
		},

		getPropertyValue:
			(state) =>
			(property, isData = false, legacyAnalytics = false) => {
				let values = isData ? state.values.data[property] : state.values[property]
				if (legacyAnalytics && property in VALUES_MAP_FOR_LEGACY_ANALYTICS_BY_PROPERTY) {
					try {
						// Replace new values with old values for legacy analytics events DEV-2000
						if (Array.isArray(values)) {
							values = values.map((v) => {
								return VALUES_MAP_FOR_LEGACY_ANALYTICS_BY_PROPERTY[property][v] ?? v
							})
						} else {
							values = VALUES_MAP_FOR_LEGACY_ANALYTICS_BY_PROPERTY[property][values] ?? values
						}
					} catch (e) {
						Sentry.captureException(e)
					}
				}
				return values
			},

		userWeightInKg: (state) => {
			return state.values.weight
		},

		userTargetWeightInKg: (state) => {
			return state.values.targetWeight
		},

		userWeightByUnitSystem: (state, getters, rootState, rootGetters) =>
			convertWeights(state.values.weight, rootGetters.getWeightUnitSystem),

		userTargetWeightByUnitSystem: (state, getters, rootState, rootGetters) =>
			convertWeights(state.values.targetWeight, rootGetters.getWeightUnitSystem),

		userHeightByUnitSystem: (state, getters, rootState, rootGetters) =>
			convertHeight(state.values.height, rootGetters.getHeightUnitSystem),

		userHeightInCm: (state) => state.values.height,

		stepVisibility: (state, getters) => (step_id) => {
			let step = getters.getStep(step_id)
			if (!Object.prototype.hasOwnProperty.call(step, 'visibleIf')) return true

			// let dependent_step = getters.getStep(step.visibleIf.id)
			let dependent_value = getters.getPropertyValue(step.visibleIf.property, true)
			let validate_values = step.visibleIf.values
			return typeof dependent_value == 'object' && validate_values.indexOf(dependent_value.toString()) !== -1
		},

		healthNoticeHidden(state, getters) {
			let diseases = getters.getPropertyValue('diseases', true)
			let drugs = getters.getPropertyValue('drugs', true)
			return drugs.includes('no') && diseases.includes('none')
		},

		userSex(state, getters) {
			return getters.getPropertyValue('sex', false) || 'female'
		},

		ageRange(state, getters) {
			const age = getters.getPropertyValue('age', true)

			return ANALYTIC_AGE_TO_AGE_RANGE_MAP[age] ?? 'other'
		},

		getUserHasConsent(state) {
			return (consent) => {
				try {
					return state.values.consents.includes(consent)
				} catch (e) {
					return false
				}
			}
		}
	},

	mutations: {
		updateProperty(state, data) {
			if (data.isData) {
				this._vm.$set(state.values.data, data.property, data.value)
			} else {
				this._vm.$set(state.values, data.property, data.value)
			}
			state.changed = true
		},

		updateCurrentStep(state, id) {
			state.currentStep = id
		},

		updateCurrentProgress(state, obj) {
			state.step_progress = obj
		},

		fromOnePage(state, value) {
			state.values.data.fromOnePage = value
		},

		updateUserConsents(state, consent) {
			let consents = state.values.consents
			if (!Array.isArray(consents)) {
				consents = []
			}
			if (!consents.includes(consent)) {
				consents.push(consent)
			}

			state.values.consents = consents
		}
	},

	actions: {
		async saveSurveyData(store, params = {}) {
			let time_offset = -new Date().getTimezoneOffset() * 60
			let response = await api.updateUser({
				...store.state.values,
				locale: i18n.locale,
				secondsFromGMT: time_offset,
				plaformFirst: 'web',
				...params
			})
			return response.data
		},

		async updateSurveyData(store, data) {
			let time_offset = -new Date().getTimezoneOffset() * 60

			let response = await api.updateUser({
				...store.state.values,
				name: data.name,
				email: data.email,
				secondsFromGMT: time_offset
			})

			return response.data
		},

		async saveUserName(store, data) {
			let time_offset = -new Date().getTimezoneOffset() * 60

			let response = await api.updateUser({
				name: data.name,
				secondsFromGMT: time_offset
			})

			return response.data
		},

		async getMlChurnPredict(store) {
			const mlChurnPredictOverride = store.rootGetters['location/getQueryParam']('ml_churn_predict_override')

			const data = {
				user_information: store.state.values.data,
				country: store.rootGetters['getCountryCode']?.toUpperCase(),
				language: store.rootGetters['i18n/getLanguage'],
				os_name: os,
				debug: ENV_DEV_OR_STAGING
			}

			// only for debug purposes
			if (mlChurnPredictOverride && !isNaN(parseInt(mlChurnPredictOverride))) {
				data.override_response = parseInt(mlChurnPredictOverride)
			}

			// slash in the end of route is mandatory!
			const response = await mlChurnPredictRequest('predict_onboarding/', {
				method: 'post',
				timeout: 10000,
				data: {
					api_key: config('MlChurnPredictApiKey'),
					data
				}
			})

			return response
		}
	}
}
